import type { ProviderBranding } from '@/types/tenant';
import type { ThemeMode } from '@/hooks/useResolvedTheme';

const normalizeLogo = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? '';
  if (!trimmed.length) return '';

  // Protect production/staging from misconfigured dev-only URLs like:
  // http://{tenant}.florentin.localhost:3001/storage/logos/...
  // These will trigger mixed content warnings on HTTPS pages and won't resolve publicly anyway.
  if (typeof window !== 'undefined') {
    try {
      const url = new URL(trimmed, window.location.origin);
      const isDevAssetHost =
        url.hostname === 'localhost' || url.hostname.endsWith('.localhost');
      const isRunningOnDevHost =
        window.location.hostname === 'localhost' ||
        window.location.hostname.endsWith('.localhost');

      if (isDevAssetHost && !isRunningOnDevHost) {
        return '';
      }
    } catch {
      // If it's not a valid URL (or a relative path that URL can't parse), keep the raw string.
    }
  }

  return trimmed;
};

export function resolveBrandLogo(
  branding: ProviderBranding | null | undefined,
  theme: ThemeMode,
): string {
  const logoLight = normalizeLogo(branding?.logoLight);
  const logoDark = normalizeLogo(branding?.logoDark);
  const logoDefault = normalizeLogo(branding?.logo);

  if (theme === 'dark') {
    return logoLight || logoDefault || logoDark;
  }

  return logoDark || logoDefault || logoLight;
}
